import { Renderer, type OpenUIError } from "@openuidev/react-lang";
import { insightProgramFromRanking, validateInsightProgram } from "@tutor/genui";
import type { Locale, ProgressReport } from "@tutor/protocol";
import { Component, useCallback, useMemo, useState, type ReactNode } from "react";
import { useLocale } from "../i18n";
import type { ParentAlertRow } from "./api";
import { FiguresContext, parentCardLibrary, type Figures } from "./cards";
import { alertsInWindow, countAlerts, rankSkills } from "./ranking";
import { useParentT } from "./strings";

// The Progress tab's cards: a deterministic program built from the engine's ranking,
// validated against the shared library and the report, then rendered with the
// components in ./cards.tsx (which look every figure up in the report).

/** The deterministic card program for a report, and whether it validates. */
export function programForReport(
  report: ProgressReport,
  alerts: readonly ParentAlertRow[],
  locale: Locale,
): { source: string; valid: boolean } {
  const ranking = rankSkills(report, alertsInWindow(alerts, report));
  const source = insightProgramFromRanking(ranking, locale);
  const reported = new Set(report.skills.map((s) => s.skillId));
  return { source, valid: validateInsightProgram(source, reported).kind === "valid" };
}

export function InsightView(props: { report: ProgressReport; alerts: readonly ParentAlertRow[] }) {
  const { report, alerts } = props;
  const locale = useLocale();
  const t = useParentT();
  const program = useMemo(() => programForReport(report, alerts, locale), [report, alerts, locale]);
  const figures = useMemo<Figures>(
    () => ({ report, alertCounts: countAlerts(alertsInWindow(alerts, report)) }),
    [report, alerts],
  );
  const [failed, setFailed] = useState(false);
  const fail = useCallback(() => {
    setFailed(true);
  }, []);
  const onError = useCallback(
    (errors: OpenUIError[]) => {
      if (errors.some((error) => error.severity !== "warning")) fail();
    },
    [fail],
  );

  if (!program.valid || failed) return <p className="parent-muted">{t("progress.invalid")}</p>;
  return (
    <FiguresContext.Provider value={figures}>
      <CardBoundary onError={fail}>
        <Renderer.Root
          response={program.source}
          library={parentCardLibrary()}
          isStreaming={false}
          onError={onError}
          publishObservability={false}
        >
          <Renderer.Content />
        </Renderer.Root>
      </CardBoundary>
    </FiguresContext.Provider>
  );
}

class CardBoundary extends Component<
  { onError: () => void; children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(): void {
    this.props.onError();
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}
